import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, MapPin, MapPinPlus, Search, Globe2 } from "lucide-react";
import { PLACE_TYPES, PLACE_TYPE_META } from "../lib/placeType";
import { carriedSurfacePin } from "../lib/facilityAdd";
import { GROUND_NAME_MAX, isVenueAuthority } from "../lib/groundEdit";
import { nominatimFetch, reverseGeocodePlace } from "../lib/geocode";
import { toast } from "sonner";
import { FieldError } from "./kit/FieldError";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";

/**
 * Dialog for searching cricket grounds on OpenStreetMap (via Nominatim) and adding them
 * to the app's grounds directory (which powers the Grounds Map).
 *
 * Usage:
 *   <GroundSearchDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     initialQuery="Corvale"
 *     initialTab="search"        // optional: force 'search' | 'manual' (issue #210)
 *     onSaved={(ground) => ...}   // called after successful POST /api/grounds
 *   />
 *
 * `hideIndoor` (issue #1008, D203.1) — false by default, so this dialog's
 * legacy callers (`GroundPicker.jsx`, the pre-#858 grounds map detail
 * dialog) are byte-identical. `GroundsPlayerScreen` (`pages/GroundsMap.jsx`,
 * the "search worldwide" add tab reached from the flag-on `"grounds"`
 * screen) passes `hideIndoor={newUi("grounds")}` — its own screen's flag,
 * read at that one call site rather than inside this shared file, because
 * this dialog ALSO serves callers that are never gated on any flag at all
 * (`GroundPicker.jsx`'s legacy log/training entry points): a raw
 * `newUi("grounds")` read in here would leak into them the moment the
 * runtime default (D202, unset-means-ON) makes that flag true. This is an
 * ADD-only dialog — no existing ground to preserve a type for — so, unlike
 * `GroundEditPanel.jsx`, there is no "already indoor" exception to thread
 * through.
 *
 * Reconciled against the real `POST /api/grounds` (issue #57) for issue #58:
 * the prototype API's `ground_name` field is `name` here, and `region`/
 * `address` were never persisted columns on the D1 `grounds` table (see
 * `api/src/db/repos/grounds.ts`) — those two manual-entry fields are
 * dropped rather than kept as a silent no-op. `city`/`country` are
 * required (non-empty) by the API's zod schema, so both search-result and
 * manual submission paths now guard for that instead of 400ing.
 *
 * `GET /grounds/check-duplicate` was hardened in #57's adversarial review:
 * it now scopes to the caller's visible grounds and returns ONLY
 * `ground_id/name/city/country/latitude/longitude` — no `is_global`, no
 * `created_by`. The GLOBAL/USER badge that used to read `is_global` off a
 * duplicate match is gone because that field is deliberately no longer in
 * the response.
 *
 * ISSUE #136 — THE TYPE IS A QUESTION, ASKED ONCE, ON BOTH TABS.
 *
 * Three buttons, not a `<Select>`, and nothing preselected. A select with
 * `field` showing is a select the contributor scrolls past, and the ticket
 * is explicit that this is the one field they must consciously answer — a
 * directory where every nets facility is filed as a ground is the state
 * this issue exists to leave. So Save is disabled until one is pressed, on
 * the search tab and the manual tab alike: a place found on OpenStreetMap
 * is no more self-describing than one typed by hand (OSM's own `type` is
 * about the map feature, not about whether you can practise there).
 *
 * IT ALSO GATES THE DUPLICATE CHECK, and that is not a side effect. "Is
 * this already in the directory?" has no answer until you say what "this"
 * is — a nets facility 80 m from a field is not a duplicate of it — so
 * `GET /grounds/check-duplicate` now REQUIRES `place_type` and this dialog
 * simply does not ask until it has one. Choosing a type therefore runs the
 * check, and changing the type re-runs it against the other set.
 *
 * NOMINATIM USAGE POLICY (spec §4.5, issue #58 hard requirement):
 * requests must carry an identifying User-Agent, be debounced (>=500ms
 * after typing stops, never per-keystroke), and never fire on empty input.
 * The identifier and the request shape moved to `lib/geocode.js` for issue
 * #379 (the pin-drag correction needs the same reverse lookup, and two
 * copies of a usage-policy header is one copy too many); the debounce stays
 * here, in the effect that knows when typing stopped.
 */

/**
 * Issue #611 — the search box's text seeds the manual form's NAME field, and
 * that field is bounded at `GROUND_NAME_MAX` because the API bounds it. A
 * prefill is typing the user did not do, so it is held to the same bound:
 * without this, a pasted paragraph could arrive in a field that will not let
 * you type one, and the only feedback would be a 400 on save.
 */
function seedName(raw) {
  return String(raw ?? "").slice(0, GROUND_NAME_MAX);
}

// #1307 — the kit's invalid border (index.css `--nw-border-invalid`), with no outline or
// shadow of its own, so the shadcn Input's keyboard ring (#1334) still shows beside
// the red while the field is focused (the same shape as TrainingSessions' INVALID_STYLE).
const INVALID_STYLE = { border: "var(--nw-border-invalid)" };

/**
 * #1363 (same cause as #1355): pressing a button in the duplicate panel blurs
 * the name field, and an emptied name's blur check clears the panel in the
 * same frame. The button then moves between mousedown and mouseup and the
 * click lands on nothing. Cancelling the default of mousedown keeps focus (and
 * so the blur and its re-check) until the click completes. The panel keeps
 * its meaning: it still clears on the next real blur. Click, Enter and Space
 * are unaffected.
 */
const keepFocus = (e) => e.preventDefault();

export default function GroundSearchDialog({ open, onOpenChange, initialQuery = "", initialTab = null, presetCoords = null, onSaved, onAddSurfaceInstead = null, hideIndoor = false, markFieldErrors = false }) {
  // #1307 (D303) — false by default, so every caller but the new-look ones
  // (LogMatchScorecardStep, TrainingSessions on the new look) is byte-identical:
  // Save still toasts the first missing field and marks nothing. True marks
  // every failing manual field at once and takes the player to the first.
  const [manualErrors, setManualErrors] = useState({});
  const manualRef = useRef(null);
  const armFocus = useFocusFirstInvalid(manualRef, manualErrors);
  const { user } = useAuth();
  const canBookmark = user?.role === "player";
  /**
   * Issue #606 — WHICH DOOR THIS DIALOG POSTS THROUGH.
   *
   * `POST /api/grounds` creates every ground PRIVATE, for every caller, and
   * that is deliberate (#288, #600): a player pinning their club is not
   * publishing to a directory the whole platform reads. An admin adding a
   * venue through the admin portal IS, and this dialog was the only add
   * form, so 27 venues an admin entered to build the shared directory
   * landed invisible to everyone but him.
   *
   * The fix is the endpoint, not a flag: `POST /api/admin/grounds` sits
   * behind `requireRole("admin")` at its router and publishes what it
   * creates. This predicate only decides which URL to try — the SERVER
   * decides who may publish, and a client that guessed wrong here gets a
   * 403, never a silent publish. It rides `isVenueAuthority`, the same
   * predicate the edit and publish affordances use, so all three widen
   * together when club ownership lands (#158).
   */
  const publishesOnCreate = isVenueAuthority(user);

  const [tab, setTab] = useState("search"); // 'search' | 'manual'
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null); // one of the Nominatim results, normalised
  const [manual, setManual] = useState({ ground_name: "", city: "", country: "", latitude: "", longitude: "" });
  // Issue #136. `null` means "not answered yet", which is a real state and
  // not a synonym for `field` — Save stays disabled and the duplicate check
  // stays unasked until it becomes one of the three.
  const [placeType, setPlaceType] = useState(null);
  const [duplicates, setDuplicates] = useState([]); // possible existing matches
  const [checkingDup, setCheckingDup] = useState(false);
  /**
   * Issue #633 — "CHECKED, CLEAR" AND "THE CHECK FAILED" ARE DIFFERENT STATES.
   *
   * `duplicates` used to carry both. The catch below set it to `[]` on any
   * error, and an empty list renders as no warning panel at all — pixel for
   * pixel identical to a check that ran and found nothing. So while
   * `check-duplicate` was 500ing for every signed-in user (#633's actual
   * fault: an unbounded prefilter, one `SELECT *` per keystroke-blur), the
   * form told everybody their ground was new. Martin typed "Ravenmoor College",
   * saw silence, and the silence was a server error.
   *
   * That is the #608 / #517 false-empty family, closed here the way those two
   * closed it: the failure is a THIRD state with its own panel and its own
   * retry, and an empty list may only ever mean the server said empty.
   *
   * It does NOT block Save. A duplicate warning is advisory — it always has
   * been, on every path — and refusing to let anyone add a ground while one
   * endpoint is down trades a silent risk for a certain outage. What the user
   * gets is the truth: we could not check, here is the button to try again.
   */
  const [dupCheckFailed, setDupCheckFailed] = useState(false);
  /** The last question asked, so the retry can re-ask it verbatim. */
  const lastDupQuery = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Issue #136: `type` is required by the endpoint. Called with none — which
  // is every call before the contributor has answered — this clears the
  // panel rather than asking a question the server would 400.
  const checkForDuplicates = useCallback(async (query) => {
    const { type, name, latitude, longitude } = query;
    // #633: a question we decline to ask is not a question that failed. Both
    // early returns below clear the failure state along with the list —
    // otherwise a stale error would outlive the input that caused it.
    lastDupQuery.current = null;
    if (!type) { setDupCheckFailed(false); return setDuplicates([]); }
    const params = new URLSearchParams();
    params.set("place_type", type);
    if (name?.trim()) params.set("name", name.trim());
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      params.set("latitude", String(latitude));
      params.set("longitude", String(longitude));
    }
    // `place_type` alone is not a question — it constrains an answer, it
    // does not ask for one. Something to match on has to be present too.
    if ([...params.keys()].length < 2) { setDupCheckFailed(false); return setDuplicates([]); }
    lastDupQuery.current = query;
    setCheckingDup(true);
    try {
      const { data } = await api.get(`/grounds/check-duplicate?${params.toString()}`);
      setDuplicates(data || []);
      setDupCheckFailed(false);
    } catch {
      // #633: the list is cleared AND the failure is recorded. Clearing
      // alone is what made a 500 look like an all-clear.
      setDuplicates([]);
      setDupCheckFailed(true);
    } finally {
      setCheckingDup(false);
    }
  }, []);

  /** #633 — re-ask the last question, verbatim. */
  const retryDuplicateCheck = useCallback(() => {
    if (lastDupQuery.current) checkForDuplicates(lastDupQuery.current);
  }, [checkForDuplicates]);

  // Reset when opened — if the caller passed presetCoords (e.g. dropped a pin on the map),
  // jump straight to the manual tab, prefill lat/lng, and try to reverse-geocode city/country.
  //
  // `presetCoords.fromDevice` (issue #134) marks the one caller whose pin is
  // a reading of where the PLAYER is rather than where they clicked. It
  // travels on the pin object rather than as a separate prop precisely so
  // it cannot desync from the coordinates it describes. See the
  // `checkForDuplicates` call below for what it changes and why.
  useEffect(() => {
    if (!open) return undefined;
    setQuery(initialQuery);
    setResults([]);
    setSelected(null);
    setDuplicates([]);
    // #633: a reopened dialog has not failed a check yet either.
    setDupCheckFailed(false);
    // Issue #136: a reopened dialog asks again. Carrying the last answer
    // over would make the second ground added in a session the one nobody
    // looked at — the same failure as a preselected default, arriving later.
    setPlaceType(null);

    if (presetCoords && Number.isFinite(presetCoords.lat) && Number.isFinite(presetCoords.lng)) {
      setTab("manual");
      setManual({
        ground_name: seedName(initialQuery),
        city: "",
        country: "",
        latitude: String(presetCoords.lat.toFixed(6)),
        longitude: String(presetCoords.lng.toFixed(6)),
      });
      // Best-effort reverse-geocode via Nominatim — one-shot on open, not
      // typing-driven, so no debounce is needed here.
      const ac = new AbortController();
      abortRef.current = ac;
      reverseGeocodePlace(presetCoords.lat, presetCoords.lng, ac.signal)
        .then((place) => {
          // `null` is "the lookup had nothing to say" (offline, rate-limited,
          // aborted) — leave what is there rather than blanking it.
          if (!place) return;
          setManual((m) => ({
            ...m,
            city: place.city || m.city,
            country: place.country || m.country,
          }));
        })
        .catch(() => { /* offline / rate-limited — user can just type it */ });
      // Issue #134, adversarial review: the duplicate check is name-only
      // when the pin IS the player's device position.
      //
      // `GET /grounds/check-duplicate?latitude=&longitude=` puts a live
      // position into OUR Worker's request logs — verbatim the exposure
      // `lib/nearMe.js` exists to prevent, and worse than the sanctioned
      // Nominatim call above because it is our own retention. On the
      // near-me path those coordinates are also provably dead: "Add this
      // place" is only reachable when nothing was found within 50 km, and
      // `findDuplicateGrounds` is a ±0.005° (~500 m) box — a coordinate
      // match is impossible by construction, so the parameters could only
      // ever have cost privacy and never bought an answer.
      //
      // A hand-dropped pin is unaffected: those are coordinates the user
      // chose by clicking a map, not a reading of where their body is, and
      // there the box genuinely can catch "you just pinned a ground that
      // already exists 200 m away".
      //
      // Issue #136: no `type` yet on open, so this run clears the panel and
      // the real check happens the moment one is chosen (the effect below).
      // The near-me/hand-dropped distinction is preserved there.
      checkForDuplicates(
        presetCoords.fromDevice
          ? { name: initialQuery }
          : { latitude: presetCoords.lat, longitude: presetCoords.lng, name: initialQuery },
      );
    } else {
      // `initialTab` lets a caller land the user on a named path in one tap
      // (issue #210: the picker's no-match state offers "Search worldwide"
      // and "Enter manually" as separate rows). Absent that, the original
      // heuristic stands: a seeded query means the name is already known, so
      // manual entry is the shorter road and needs no network.
      setTab(initialTab || (initialQuery ? "manual" : "search"));
      setManual({ ground_name: seedName(initialQuery), city: "", country: "", latitude: "", longitude: "" });
      setManualErrors({});
      if (initialQuery) checkForDuplicates({ name: initialQuery });
    }
    return undefined;
  }, [open, initialQuery, initialTab, presetCoords, checkForDuplicates]);

  /**
   * Issue #136 — the check the two calls above could not make yet.
   *
   * Runs when a type is chosen and again when it is changed, because the
   * answer is genuinely different per type: that is the whole exclusion
   * rule. Keyed on the values it reads rather than on the dialog opening,
   * so it fires once per real change and not on every keystroke — the name
   * is still re-checked on blur, as before.
   *
   * The `fromDevice` rule from #134's adversarial review survives intact:
   * a pin that is a reading of where the PLAYER is contributes its name and
   * never its coordinates, so a live position still cannot reach our own
   * request logs through this endpoint.
   */
  useEffect(() => {
    if (!open || !placeType) return;
    if (presetCoords && Number.isFinite(presetCoords.lat) && Number.isFinite(presetCoords.lng)) {
      checkForDuplicates(
        presetCoords.fromDevice
          ? { type: placeType, name: initialQuery }
          : { type: placeType, name: initialQuery, latitude: presetCoords.lat, longitude: presetCoords.lng },
      );
      return;
    }
    checkForDuplicates({ type: placeType, name: initialQuery });
  }, [open, placeType, presetCoords, initialQuery, checkForDuplicates]);

  const runSearch = useCallback(async (termArg) => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    const term = (termArg ?? query).trim();
    if (!term) { setResults([]); return; }
    // cancel any inflight
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(term)}&format=json&limit=8&addressdetails=1`;
      const resp = await nominatimFetch(url, controller.signal);
      const data = await resp.json();
      const mapped = data.map((r) => ({
        id: r.place_id,
        name: pickName(r),
        display_name: r.display_name,
        city: r.address?.city || r.address?.town || r.address?.village || r.address?.municipality || r.address?.county || "",
        country: r.address?.country || "",
        latitude: parseFloat(r.lat),
        longitude: parseFloat(r.lon),
        type: r.type,
      })).filter((x) => Number.isFinite(x.latitude) && Number.isFinite(x.longitude));
      setResults(mapped);
      if (mapped.length === 0) toast.info("No matches on OpenStreetMap — try a broader search or use manual entry.");
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("Nominatim search failed", e);
        toast.error("Search failed. Please try again.");
      }
    } finally {
      setSearching(false);
    }
  }, [query]);

  // Nominatim usage policy (hard requirement, issue #58): auto-search fires
  // only >=500ms after the user stops typing, never per keystroke, and
  // never on an empty query. The Enter/button paths below call runSearch()
  // directly for an immediate, explicit, user-initiated submit — that's a
  // single action, not "hammering it from keystrokes" — and runSearch
  // itself clears any pending debounce timer so a manual submit can't be
  // followed by a duplicate auto-fire.
  useEffect(() => {
    if (tab !== "search") return undefined;
    const term = query.trim();
    if (!term) { setResults([]); return undefined; }
    debounceRef.current = setTimeout(() => { runSearch(term); }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, tab, runSearch]);

  const submitSelected = async () => {
    if (!selected) return;
    // Issue #136. The button is disabled without a type; this is the second
    // line, so a keyboard/test path cannot post a ground with no answer.
    if (!placeType) return toast.error("Choose what kind of place this is");
    await saveGround({
      name: selected.name,
      city: selected.city || "Unknown",
      country: selected.country || "Unknown",
      latitude: selected.latitude,
      longitude: selected.longitude,
      place_type: placeType,
    });
  };

  const submitManual = async () => {
    const lat = parseFloat(manual.latitude);
    const lng = parseFloat(manual.longitude);
    if (markFieldErrors && placeType) {
      const map = {};
      if (!manual.ground_name?.trim()) map.ground_name = "Ground name required";
      if (!manual.city?.trim()) map.city = "City / Town required";
      if (!manual.country?.trim()) map.country = "Country required";
      if (!Number.isFinite(lat)) map.latitude = "Latitude and longitude are required (numbers)";
      else if (lat < -90 || lat > 90) map.latitude = "Coordinates out of range";
      if (!Number.isFinite(lng)) map.longitude = "Latitude and longitude are required (numbers)";
      else if (lng < -180 || lng > 180) map.longitude = "Coordinates out of range";
      if (Object.keys(map).length) {
        setManualErrors(map);
        armFocus();
        return;
      }
    }
    if (!placeType) return toast.error("Choose what kind of place this is");
    if (!manual.ground_name?.trim()) return toast.error("Ground name required");
    if (!manual.city?.trim()) return toast.error("City / Town required");
    if (!manual.country?.trim()) return toast.error("Country required");
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return toast.error("Latitude and longitude are required (numbers)");
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return toast.error("Coordinates out of range");
    await saveGround({
      name: manual.ground_name.trim(),
      city: manual.city.trim(),
      country: manual.country.trim(),
      latitude: lat, longitude: lng,
      place_type: placeType,
    });
  };

  // #1307 — the mark for one manual field; {} unless markFieldErrors and the field has an entry.
  const markOf = (key, testId) =>
    markFieldErrors && manualErrors[key]
      ? { "aria-invalid": "true", "aria-describedby": `${testId}-error`, "data-nw-invalid": "", style: INVALID_STYLE }
      : {};
  const errOf = (key, testId) =>
    markFieldErrors && manualErrors[key] ? (
      <FieldError id={`${testId}-error`} testId={`${testId}-error`}>{manualErrors[key]}</FieldError>
    ) : null;
  const clearErr = (key) => {
    if (manualErrors[key]) setManualErrors((m) => ({ ...m, [key]: undefined }));
  };

  const saveGround = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post(publishesOnCreate ? "/admin/grounds" : "/grounds", payload);
      toast.success(
        publishesOnCreate
          ? `"${data.name}" published to the shared directory`
          : `"${data.name}" added to your Grounds Map`,
      );
      onSaved?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add ground");
    } finally {
      setSaving(false);
    }
  };

  // Linking to an existing duplicate must actually persist the bookmark
  // (issue #58 fix) — the restored dialog used to just call onSaved() here
  // without ever hitting the API, so the "link" silently didn't survive a
  // reload. Bookmarking only applies to the player role (see
  // `players/me/grounds` — 404s for a caller with no player profile).
  const linkDuplicate = async (g) => {
    if (!canBookmark) {
      onSaved?.(g);
      onOpenChange?.(false);
      return;
    }
    setSaving(true);
    try {
      await api.post("/players/me/grounds", { ground_id: g.ground_id });
      toast.success(`Linked to "${g.name}"`);
      onSaved?.(g);
      onOpenChange?.(false);
    } catch {
      toast.error("Could not save this ground");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Issue #547 — the third way out of the duplicate prompt: the venue
   * already exists, and what you are standing at is one of its SURFACES.
   *
   * Registering a club's second field or nets bay as a new ground is the
   * exact mistake nothing else in the app catches, and this is the moment
   * it is about to be made — so beside "use this one" and the implicit
   * "save anyway", the host may offer routing INTO the existing ground
   * instead (`onAddSurfaceInstead`, rendered only when a host passes it:
   * the grounds page can land you in the ground's detail dialog; the
   * match-logging picker has nowhere honest to send you mid-log and keeps
   * the two-option prompt).
   *
   * The draft's pin travels along (`carriedSurfacePin`) — the tap that
   * placed the would-be ground is exactly where the surface is, and losing
   * it would make the right choice cost more than the wrong one. The
   * ground draft itself is abandoned by closing this dialog: the open
   * effect resets every field on the next open, and the host's
   * `onOpenChange(false)` clears its seed and pending pin.
   */
  const addSurfaceInstead = (g) => {
    if (!onAddSurfaceInstead) return;
    onAddSurfaceInstead(g, carriedSurfacePin(presetCoords, manual));
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" bodyClassName={markFieldErrors ? "px-1 -mx-1" : undefined} data-testid="ground-search-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe2 size={18} color="#0B2E59" />
            Add a ground you play at
          </DialogTitle>
          {/* Issue #547 — the model, said where grounds are made: Millwater
              is ONE ground, and its fields and nets live inside it. One
              line, both tabs, every host of this dialog. */}
          <p className="text-xs text-slate-500" data-testid="gsd-one-ground-note">
            One ground per place — fields and nets are added inside it.
          </p>
        </DialogHeader>

        <div className="border-b border-slate-200">
          <div className="flex gap-1">
            <TabButton active={tab === "search"} onClick={() => setTab("search")} testid="gsd-tab-search">
              <Search size={13} className="mr-1.5" /> Search the globe
            </TabButton>
            <TabButton active={tab === "manual"} onClick={() => setTab("manual")} testid="gsd-tab-manual">
              <MapPin size={13} className="mr-1.5" /> Enter manually
            </TabButton>
          </div>
        </div>

        {tab === "search" && (
          <div className="space-y-3">
            <div>
              <Label>Search any cricket field, stadium or landmark</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="gsd-query"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                  placeholder='e.g. "Driftmoor Stadium, Hamilton"'
                />
                <Button onClick={() => runSearch()} disabled={searching || !query.trim()} data-testid="gsd-search-btn" style={{ background: "#0B2E59" }}>
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Powered by OpenStreetMap · Nominatim. Searches automatically ~500ms after you stop typing. Please respect their fair-use policy.
              </p>
            </div>

            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="gsd-results">
              {results.length === 0 && !searching && (
                <p className="text-sm text-slate-500 italic py-4 text-center">
                  Try searching for a stadium, cricket field or the city + ground name.
                </p>
              )}
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className={`w-full text-left rounded-lg border p-3 transition ${selected?.id === r.id ? "border-[#0B2E59] bg-[#0B2E5908]" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`gsd-result-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                      <div className="text-xs text-slate-500 truncate">{r.display_name}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-[11px] font-mono text-slate-600">{r.latitude.toFixed(3)}, {r.longitude.toFixed(3)}</div>
                      {r.type && <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.type}</div>}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <PlaceTypeChooser value={placeType} onChange={setPlaceType} hideIndoor={hideIndoor} />

            {/* #606 — say which directory this lands in, on the screen
                where the decision is made. An admin adding a venue is
                publishing it to everyone; a player is pinning it for
                themselves. Both were silent before, and the silence is what
                cost 27 venues. */}
            <p className="text-[11px] text-slate-500" data-testid="gsd-visibility-note">
              {publishesOnCreate
                ? "Added as an admin: this venue goes into the shared directory and every signed-in user will see it."
                : "Only you will see this ground until an admin publishes it to the shared directory."}
            </p>


            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
              <Button
                onClick={submitSelected}
                disabled={!selected || !placeType || saving}
                style={{ background: "#0B2E59" }}
                data-testid="gsd-save-selected"
              >
                {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <MapPinPlus size={14} className="mr-2" />}
                Add selected ground
              </Button>
            </DialogFooter>
          </div>
        )}

        {tab === "manual" && (
          <div className="space-y-3" ref={manualRef}>
            {/* #633 — the check FAILED, said as a failure. Rendered where the
                warning would have been, because the reader's question is the
                same one ("is this already here?") and the honest answer is
                "we do not know". */}
            {dupCheckFailed && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 p-3"
                data-testid="gsd-duplicate-check-failed"
                role="alert"
              >
                <div className="text-[11px] uppercase tracking-widest font-bold text-rose-700 mb-1.5">
                  ⚠ Couldn&apos;t check for duplicates
                </div>
                <p className="text-xs text-rose-800/80 mb-2">
                  We couldn&apos;t reach the grounds directory, so we don&apos;t know whether this place is
                  already on it. You can still save — just check the map first if you can.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-rose-200"
                  onClick={retryDuplicateCheck}
                  onMouseDown={keepFocus}
                  disabled={checkingDup}
                  data-testid="gsd-duplicate-check-retry"
                >
                  {checkingDup ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                  Check again
                </Button>
              </div>
            )}
            {duplicates.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="gsd-duplicate-warning">
                <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800 mb-1.5 flex items-center gap-1.5">
                  ⚠ Similar ground{duplicates.length > 1 ? "s" : ""} already exists
                </div>
                <p className="text-xs text-amber-800/80 mb-2">Use an existing entry to avoid duplicates:</p>
                <div className="space-y-1.5">
                  {duplicates.map((g) => (
                    <div key={g.ground_id} className="rounded border border-amber-200 bg-white p-2">
                      <div className="font-semibold text-sm text-slate-900">{g.name}</div>
                      <div className="text-[11px] text-slate-500">{[g.city, g.country].filter(Boolean).join(", ") || "—"}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => linkDuplicate(g)}
                          onMouseDown={keepFocus}
                          disabled={saving}
                          className="inline-flex items-center rounded border border-amber-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-700 hover:border-amber-400 hover:bg-amber-50 transition disabled:opacity-60"
                          data-testid={`gsd-duplicate-${g.ground_id}`}
                        >
                          Use this ground
                        </button>
                        {/* #547 — the third option. Rendered only when the host
                            can actually route there (see addSurfaceInstead). */}
                        {onAddSurfaceInstead && (
                          <button
                            type="button"
                            onClick={() => addSurfaceInstead(g)}
                            onMouseDown={keepFocus}
                            disabled={saving}
                            className="inline-flex items-center rounded border border-amber-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-700 hover:border-amber-400 hover:bg-amber-50 transition disabled:opacity-60"
                            data-testid={`gsd-duplicate-add-surface-${g.ground_id}`}
                          >
                            Add a field or net to {g.name} instead
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <PlaceTypeChooser value={placeType} onChange={setPlaceType} hideIndoor={hideIndoor} />

            {/* #606 — say which directory this lands in, on the screen
                where the decision is made. An admin adding a venue is
                publishing it to everyone; a player is pinning it for
                themselves. Both were silent before, and the silence is what
                cost 27 venues. */}
            <p className="text-[11px] text-slate-500" data-testid="gsd-visibility-note">
              {publishesOnCreate
                ? "Added as an admin: this venue goes into the shared directory and every signed-in user will see it."
                : "Only you will see this ground until an admin publishes it to the shared directory."}
            </p>

            <div>
              <Label>Ground name *</Label>
              {/* Issue #611 — bounded at the API's `GROUND_NAME_MAX_LENGTH`.
                  `onBlur` here fires `GET /grounds/check-duplicate`, which
                  binds one SQL parameter per word of what is typed; a name
                  past the bound is a 400 from that endpoint AND from the
                  create, so the field stops before either. */}
              <Input value={manual.ground_name}
                maxLength={GROUND_NAME_MAX}
                onChange={(e) => { setManual({ ...manual, ground_name: e.target.value }); clearErr("ground_name"); }}
                onBlur={() => checkForDuplicates({ type: placeType, name: manual.ground_name, latitude: parseFloat(manual.latitude), longitude: parseFloat(manual.longitude) })}
                data-testid="gsd-m-name" {...markOf("ground_name", "gsd-m-name")} />
              {errOf("ground_name", "gsd-m-name")}
              {/* Issue #645 — `maxLength` alone makes the field go dead with
                  no explanation: pasting a long name looks like the field
                  broke, not like a rule. A counter says the bound before you
                  hit it, and the sentence at the bound says WHY it exists —
                  the duplicate check binds one SQL parameter per word of a
                  name, and one absurd cell took a whole import preview down
                  before this cap existed (#611). */}
              <p className="text-[11px] text-slate-500 mt-1" data-testid="gsd-m-name-counter">
                {manual.ground_name.length >= GROUND_NAME_MAX
                  ? `${GROUND_NAME_MAX} characters — that's the limit, so a name can't be long enough to break the duplicate check.`
                  : `${manual.ground_name.length}/${GROUND_NAME_MAX} characters`}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>City / Town *</Label><Input value={manual.city} onChange={(e) => { setManual({ ...manual, city: e.target.value }); clearErr("city"); }} data-testid="gsd-m-city" {...markOf("city", "gsd-m-city")} />{errOf("city", "gsd-m-city")}</div>
              <div><Label>Country *</Label><Input value={manual.country} onChange={(e) => { setManual({ ...manual, country: e.target.value }); clearErr("country"); }} data-testid="gsd-m-country" {...markOf("country", "gsd-m-country")} />{errOf("country", "gsd-m-country")}</div>
              <div><Label>Latitude *</Label><Input type="text" inputMode="decimal" placeholder="e.g. -36.848" value={manual.latitude} onChange={(e) => { setManual({ ...manual, latitude: e.target.value }); clearErr("latitude"); }} data-testid="gsd-m-lat" {...markOf("latitude", "gsd-m-lat")} />{errOf("latitude", "gsd-m-lat")}</div>
              <div><Label>Longitude *</Label><Input type="text" inputMode="decimal" placeholder="e.g. 174.763" value={manual.longitude} onChange={(e) => { setManual({ ...manual, longitude: e.target.value }); clearErr("longitude"); }} data-testid="gsd-m-lng" {...markOf("longitude", "gsd-m-lng")} />{errOf("longitude", "gsd-m-lng")}</div>
            </div>
            <p className="text-xs text-slate-500">
              Tip: right-click any spot on Google Maps → copy the coordinate pair.
              {checkingDup && <span className="ml-2 italic">Checking for duplicates…</span>}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
              {/* #1355: pressing Save blurs the name field, and that blur re-asks the
                  duplicate check, which can clear the warning above the button in
                  the same frame. The button then moves between mousedown and
                  mouseup and the click lands on nothing. Keeping focus where it is
                  on mousedown keeps the layout still until the click completes. */}
              <Button onClick={submitManual} onMouseDown={(e) => e.preventDefault()} disabled={saving || !placeType} style={{ background: "#0B2E59" }} data-testid="gsd-save-manual">
                {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <MapPinPlus size={14} className="mr-2" />}
                Save new ground
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Issue #136 — the one question a contributor must consciously answer.
 *
 * Three buttons rather than a `<Select>`: a select shows one value before
 * anybody has chosen anything, and a value on screen is a value that gets
 * accepted. Nothing here is pressed until the person presses it, and the
 * selected state is carried by border + weight + a check mark rather than
 * colour alone.
 *
 * `hideIndoor` (issue #1008, D203.1) — false by default (every legacy
 * caller of `GroundSearchDialog` above). `PLACE_TYPES` itself
 * (`lib/placeType.js`) is untouched: the server still accepts "indoor",
 * and this is add-only, so there is no existing ground's type to protect
 * (contrast `GroundEditPanel.jsx`'s own filter, which is).
 */
function PlaceTypeChooser({ value, onChange, hideIndoor = false }) {
  const visibleTypes = hideIndoor ? PLACE_TYPES.filter((t) => t !== "indoor") : PLACE_TYPES;
  return (
    <div data-testid="gsd-place-type">
      <Label>What kind of place is this? *</Label>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {visibleTypes.map((t) => {
          const meta = PLACE_TYPE_META[t];
          const active = value === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => onChange(t)}
              aria-pressed={active}
              data-testid={`gsd-place-type-${t}`}
              className={`text-left rounded-lg border p-2.5 min-h-11 transition ${active
                ? "border-[#0B2E59] bg-[#0B2E5908] ring-1 ring-[#0B2E59]"
                : "border-slate-200 hover:border-slate-400"}`}
            >
              <div className={`text-sm ${active ? "font-bold text-[#0B2E59]" : "font-semibold text-slate-800"}`}>
                {active ? "\u2713 " : ""}{meta.label}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{meta.hint}</div>
            </button>
          );
        })}
      </div>
      {!value && (
        <p className="text-[11px] text-slate-500 mt-1" data-testid="gsd-place-type-required">
          Pick one — it&apos;s how people tell a ground from a practice facility.
        </p>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center px-3 py-2 text-sm font-semibold border-b-2 transition -mb-px ${active ? "border-[#0B2E59] text-[#0B2E59]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
    >
      {children}
    </button>
  );
}

// Try to pick a concise ground name out of Nominatim's verbose "display_name".
function pickName(r) {
  if (r.namedetails?.name) return r.namedetails.name;
  const first = (r.display_name || "").split(",")[0].trim();
  return first || r.display_name || "Unknown ground";
}
